import { Check, Clock, ImageIcon, Truck, Wrench, X } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { cn } from "@/utils/cn";
import type { FilaAprovacaoItem } from "@/services/locacoes";
import { locacoesPanelClass } from "./locacoesUtils";
import { upperLocacaoText } from "./upperLocacaoText";
import { formatDate } from "@/utils/format";

interface Props {
  items: FilaAprovacaoItem[];
  busy?: boolean;
  canApprove?: boolean;
  onAprovar: (item: FilaAprovacaoItem) => Promise<void>;
  onRejeitar: (item: FilaAprovacaoItem, motivo: string) => Promise<void>;
  onConferirFotos?: (item: FilaAprovacaoItem) => void;
}

function filaIcon(tipo: FilaAprovacaoItem["tipo"]) {
  if (tipo === "horimetro") return { Icon: Clock, box: "bg-teal-50 text-teal-600" };
  if (tipo === "carrada") return { Icon: Truck, box: "bg-amber-50 text-amber-700" };
  return { Icon: Wrench, box: "bg-red-50 text-red-600" };
}

export function FilaAprovacaoWidget({ items, busy, canApprove = false, onAprovar, onRejeitar, onConferirFotos }: Props) {
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [motivo, setMotivo] = useState("");
  const pendentes = items.filter((i) => i.statusAprovacao === "pendente");

  const handleReject = async (item: FilaAprovacaoItem) => {
    if (!motivo.trim()) return;
    await onRejeitar(item, motivo.trim());
    setRejectingId(null);
    setMotivo("");
  };

  return (
    <div className={cn(locacoesPanelClass, "border-l-4 border-l-amber-400")}>
      <h3 className="mb-3 flex items-center gap-2 text-sm font-bold text-slate-800">
        <Clock className="h-4 w-4 text-amber-600" />
        Fila de aprovação
        {pendentes.length > 0 && (
          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
            {pendentes.length}
          </span>
        )}
      </h3>
      {!canApprove && pendentes.length > 0 && (
        <p className="mb-3 text-xs text-slate-500">
          Apontamentos de operadores aguardam aprovação por Engenharia, Master ou Administrador.
        </p>
      )}
      {pendentes.length === 0 ? (
        <p className="text-sm text-slate-500">Nenhum apontamento ou manutenção aguardando aprovação.</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {pendentes.slice(0, 8).map((item) => (
            <li key={`${item.tipo}-${item.id}`} className="py-3">
              <div className="flex items-start gap-2">
                <span
                  className={cn(
                    "mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg",
                    filaIcon(item.tipo).box,
                  )}
                >
                  {(() => {
                    const { Icon } = filaIcon(item.tipo);
                    return <Icon className="h-3.5 w-3.5" />;
                  })()}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-slate-800">{item.titulo}</p>
                  <p className="text-xs text-slate-600">{item.maquinaNome}</p>
                  <p className="text-xs text-slate-500">{item.subtitulo}</p>
                  <p className="mt-0.5 text-[10px] text-slate-400">
                    {item.solicitanteNome} • {formatDate(item.dataHora.slice(0, 10))}
                    {item.contratoLabel ? ` • ${item.contratoLabel}` : ""}
                    {(item.tipo === "horimetro" || item.tipo === "carrada") && (item.fotosCount ?? 0) > 0
                      ? ` • ${item.fotosCount} foto${item.fotosCount === 1 ? "" : "s"}`
                      : ""}
                  </p>
                </div>
              </div>
              {canApprove && rejectingId === item.id ? (
                <div className="mt-2 space-y-2 pl-9">
                  <input
                    type="text"
                    value={motivo}
                    onChange={(e) => setMotivo(upperLocacaoText(e.target.value))}
                    placeholder="Motivo da rejeição…"
                    className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm"
                  />
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={() => {
                        setRejectingId(null);
                        setMotivo("");
                      }}
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      disabled={!motivo.trim() || busy}
                      onClick={() => void handleReject(item)}
                    >
                      Confirmar rejeição
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="mt-2 flex flex-wrap gap-2 pl-9">
                  {item.tipo === "horimetro" && onConferirFotos && (
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      disabled={busy}
                      leftIcon={<ImageIcon className="h-3.5 w-3.5" />}
                      onClick={() => onConferirFotos(item)}
                    >
                      {(item.fotosCount ?? 0) > 0 ? "Conferir fotos" : "Fotos"}
                    </Button>
                  )}
                  {canApprove && (
                    <>
                      <Button
                        type="button"
                        size="sm"
                        disabled={busy}
                        leftIcon={<Check className="h-3.5 w-3.5" />}
                        onClick={() => void onAprovar(item)}
                      >
                        Aprovar
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        disabled={busy}
                        leftIcon={<X className="h-3.5 w-3.5" />}
                        onClick={() => setRejectingId(item.id)}
                      >
                        Rejeitar
                      </Button>
                    </>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
